import { get, map, some, isEmpty, memoize, reduce, first, split, intersectionWith } from 'lodash';

import Cart from 'models/Cart';
import CartLineItem from 'models/CartLineItem';
import CartUpsell from 'models/CartUpsell';
import UpsellsSelector from 'state/selectors/upsells';
import Recommendation from 'utils/Recommendation';
import Settings from 'utils/Settings';

import { MAX_ITEMS_LOGO_FREE_PACKAGING } from './constants';

// To avoid costly recommendation lookups, we are memoizing line items by Master Product Id + Variant Value
const getLineItemsCacheKey = ({ lineItems }) =>
  reduce(
    lineItems,
    (cacheKey, lineItem) => `${cacheKey}--${lineItem.product.masterId}-${lineItem.variant.value}`,
    ''
  );

const memoizedGetCartRecommendations = memoize(
  Recommendation.getRecommendations,
  getLineItemsCacheKey
);

const blockedProducts = (lineItems, logoFreeProductsBlocklist) =>
  intersectionWith(lineItems, logoFreeProductsBlocklist, (lineItem, blockedProduct) => {
    return get(lineItem, 'product.masterId') === get(blockedProduct, 'id');
  });

const memoizedBlockedProducts = memoize(blockedProducts, JSON.stringify);

const getFlattenedLineItems = items => reduce(items, (result, item) => [...result, item], []);

const CartSelector = class {
  static getCart = state => new Cart(get(state, ['ajaxCart', 'data', 'entities', 'cart', 0]));

  static getCartLineItems = state => {
    const items = get(state, ['ajaxCart', 'data', 'entities', 'item']);
    const flattenedItems = getFlattenedLineItems(items);
    const cartLineItems = map(flattenedItems, item => new CartLineItem(item));

    return cartLineItems;
  };

  static getCartItemsData = state => get(state, ['ajaxCart', 'data', 'entities', 'item']);

  static getIsCartOpen = state => get(state, ['ajaxCart', 'isOpen']);

  static getIsElligibleForGiftMessage = state => {
    const lineItems = CartSelector.getCartLineItems(state);

    return some(lineItems, ({ product }) => product.isElligibleForGiftMessage);
  };

  static getIsElligibleForLogoFree = (state, logoFreeProductsBlocklist) => {
    const lineItems = CartSelector.getCartLineItems(state);
    const { variantIds: returnsInsuranceVariantIds } = Settings.returnsInsuranceConfig || {};
    const filteredLineItems = returnsInsuranceVariantIds
      ? lineItems.filter(item => !returnsInsuranceVariantIds.includes(item.id))
      : lineItems;
    const tooManyLineItems = filteredLineItems.length >= MAX_ITEMS_LOGO_FREE_PACKAGING;

    const blockedItems = memoizedBlockedProducts(lineItems, logoFreeProductsBlocklist);

    return isEmpty(blockedItems) && !tooManyLineItems;
  };

  static getCartRecommendation = state => {
    const upsellMasterProducts = UpsellsSelector.getUpsellMasterProducts(state);

    if (isEmpty(upsellMasterProducts)) {
      return null;
    }

    const lineItems = CartSelector.getCartLineItems(state);

    if (isEmpty(lineItems)) {
      return null;
    }

    const recommendations = memoizedGetCartRecommendations({ upsellMasterProducts, lineItems });

    return first(recommendations);
  };

  static getCartPasswordList = state => {
    const passwordString = get(state, [
      'ajaxCart',
      'data',
      'entities',
      'cart',
      '0',
      'attributes',
      'password',
    ]);

    return passwordString ? split(passwordString, ',') : [];
  };

  static getIsBeingFetched = state => {
    return get(state, ['ajaxCart', 'upsells', 'isBeingFetched']);
  };

  static getRecommendationPairingNames = state => {
    const cartRecommendations = get(state, ['ajaxCart', 'upsells', 'data', 'cartRecommendations']);
    return map(cartRecommendations, cartRecommendation => {
      return get(cartRecommendation, 'name');
    });
  };

  static getCartUpsells = state => {
    const cartRecommendations = get(state, ['ajaxCart', 'upsells', 'data', 'cartRecommendations']);
    return map(cartRecommendations, cartRecommendation => {
      return map(get(cartRecommendation, 'recommendedItems'), recommendedItems => {
        return new CartUpsell(recommendedItems);
      });
    });
  };

  static getStorefrontCartCheckoutUrl = state => {
    return get(state, ['ajaxCart', 'data', 'entities', 'cart', 0, 'checkoutUrl']);
  };

  static getReturnsInsuranceVariantToRemove = state => {
    const itemIds = CartSelector.getCart(state).itemIds;
    const { variantIds } = Settings.returnsInsuranceConfig;
    const returnsInsuranceVariantToRemove =
      itemIds.length === 2 && itemIds.find(id => variantIds.includes(id));

    return returnsInsuranceVariantToRemove;
  };

  static getReturnsInsuranceRendered = state =>
    get(state, ['ajaxCart', 'returnsInsuranceRendered'], false);
};

export { memoizedBlockedProducts };
export default CartSelector;
